/**
 * PanelErrorState - The Intelligent Standard
 *
 * Error state display for panel contexts.
 * Includes timeout detection and appropriate messaging.
 */

import { IconCircle } from './IconCircle';

export interface PanelErrorStateProps {
  label: string;
  error: string;
  executionTimeMs?: number;
  /** When true, renders without outer container (for embedded use) */
  embedded?: boolean;
}

export function PanelErrorState({
  label,
  error,
  executionTimeMs,
  embedded = false,
}: PanelErrorStateProps) {
  // Detect timeout errors
  const isTimeout =
    error?.toLowerCase().includes('timed out') ||
    error?.toLowerCase().includes('timeout') ||
    (executionTimeMs && executionTimeMs >= 180000);

  const errorTitle = isTimeout ? `${label} - Timed Out` : `${label} - Error`;
  const errorMessage = isTimeout
    ? 'This model exceeded the 3-minute time limit and was stopped. The execution took too long to complete.'
    : error;

  const errorIcon = (
    <svg className="w-6 h-6" fill="none" stroke="#C41E3A" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );

  const content = (
    <div className="text-center p-8 max-w-sm">
      <IconCircle icon={errorIcon} backgroundColor="rgba(196, 30, 58, 0.1)" />
      <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>
        {errorTitle}
      </p>
      <p className="text-sm mt-2" style={{ color: '#C41E3A' }}>
        {errorMessage}
      </p>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div
      className="h-full rounded-xl flex items-center justify-center"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
      }}
    >
      {content}
    </div>
  );
}
